import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  memo,
  useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { noop } from '@prisjakt/ui-base';
import T from '@prisjakt/ui-translate';
import { TextInput } from '@prisjakt/ui-inputs';
import CurrencySettings from '@prisjakt/ui-currency/src/market-settings';
import Rating from '@prisjakt/ui-rating';
import serialize from 'form-serialize';
import { AppState } from '../../../../reducers';
import { ProductAlertFormData, Account } from '../../types';
import { isValidEmail } from '../../../account/helpers';
import CtxLink from '../../../../components/render-context/ctx-link';
import PriceTrend from '../price-trend';
import { LoginPromoButton, Section } from '../../styles';

import * as S from './styles';
import { Maybe, UserReviewSummary } from '../../../../types';

const getDefaultPriceThreshold = (
  regularPrice: number | null | undefined, // Can this come from like the server??
  minimumStepValue: number = 0.1,
  numberOfDecimals: number = 2
): string => {
  return regularPrice
    ? (regularPrice - Math.max(regularPrice * 0.01, minimumStepValue)).toFixed(
        numberOfDecimals
      )
    : '';
};

type StoreQuery = {
  store: Maybe<{
    userReviewSummary?: Maybe<UserReviewSummary>;
  }>;
};

const numberOfStoresAboveRating = (
  prices: Maybe<StoreQuery>[] | null | undefined,
  rating: number
): number => {
  if (!prices) {
    return -1;
  }

  return prices.filter(
    price => Number(price?.store?.userReviewSummary?.rating) > rating
  ).length;
};

const STATUS_IN_STOCK = 'in_stock';

enum RadioSelections {
  IN_STOCK = 'IN_STOCK',
  PRICE_DROPS = 'PRICE_DROPS',
  PRICE_BELOW = 'PRICE_BELOW',
}

export enum AlertTypes {
  IN_STOCK = 'IN_STOCK',
  PRICE_DROPS = 'PRICE_DROPS',
  IN_STOCK_PRICE_DROPS = 'IN_STOCK_PRICE_DROPS',
}

enum Errors {
  ERROR_EMAIL_INVALID = 'error.email.invalid',
  ERROR_EMAIL_IN_USE = 'alert.email-associated-with-user',
  ERROR_THRESHOLD_TOO_HIGH = 'alert.threshold-too-high',
  ERROR_UNKNOWN = 'alert.error',
}

interface GraphQLAlertType {
  type: AlertTypes;
  priceThreshold?: number;
}

type OnCreate = (alert: {
  alertId?: number;
  productId: number;
  email?: string;
  priceStep?: number;
  type: AlertTypes;
  priceThreshold?: number;
  storeGradeThreshold?: number;
}) => void;

interface ProductAlertFormProps {
  productAlertFormData: ProductAlertFormData;
  account?: Account;
  onCreate: OnCreate; // Remove? Should be alert type export type AlertCreateOptions = {
  privacyPolicyUri?: string;
  error?: string | boolean;
  showCancel?: boolean;
  shouldAutoFocus?: boolean;
  openLoginPromo?: () => void;
  userProductAlerts?: { productId: number; alertId: number }[];
  withoutGraph?: boolean;
  disabled?: boolean;
  showAlertPriceStep?: boolean;
  showAlertStoreRating?: boolean;
}

const ProductAlertForm = (props: ProductAlertFormProps) => {
  const {
    productAlertFormData,
    account = { me: undefined, isAuthenticated: false },
    onCreate,
    privacyPolicyUri,
    error,
    showCancel = true,
    shouldAutoFocus,
    openLoginPromo = noop,
    userProductAlerts = [],
    withoutGraph,
    disabled,
    showAlertPriceStep,
    showAlertStoreRating,
  } = props;

  const product = productAlertFormData.product;

  const context = useContext(AppContext);

  const emailInput = useRef<HTMLInputElement>(null);

  const productInStock = product?.stockStatus === STATUS_IN_STOCK;
  const { t } = context;
  const { isAuthenticated } = account || {};
  const { minimumStepValue, numberOfDecimals } = CurrencySettings[
    context.market.code
  ];
  const defaultPriceStep = minimumStepValue * 10;
  const [errors, setErrors] = useState<Set<Errors>>(new Set());
  const [email, setEmail] = useState<string>('');
  const [chosenStoreGradeThreshold, setChosenStoreGradeThreshold] = useState<
    number
  >(product?.alert?.storeGradeThreshold ?? 0);
  const [radioSelection, setRadioSelection] = useState<RadioSelections>(() => {
    let initialRadioSelection;
    if (product?.alert) {
      const alertType = product?.alert.alertType;
      if (alertType === AlertTypes.IN_STOCK) {
        initialRadioSelection = RadioSelections.IN_STOCK;
      } else if (
        product?.alert?.priceThreshold &&
        product?.alert?.priceThreshold > 0
      ) {
        initialRadioSelection = RadioSelections.PRICE_BELOW;
      } else {
        initialRadioSelection = RadioSelections.PRICE_DROPS;
      }
    } else if (productInStock) {
      initialRadioSelection = RadioSelections.PRICE_DROPS;
    } else {
      initialRadioSelection = RadioSelections.IN_STOCK;
    }

    return initialRadioSelection;
  });
  const [priceThreshold, setPriceThreshold] = useState<string>(() => {
    return product?.alert?.priceThreshold
      ? String(product.alert.priceThreshold)
      : getDefaultPriceThreshold(
          product?.priceSummary.regular,
          minimumStepValue,
          numberOfDecimals
        );
  });
  const [onlyAlertIfInStock, setOnlyAlertIfInStock] = useState<boolean>(
    product?.alert?.alertType !== AlertTypes.PRICE_DROPS
  );

  const [priceStepCheckbox, setPriceStepCheckbox] = useState<boolean>(
    !!product?.alert?.priceStep
  );

  const [priceStep, setPriceStep] = useState<string>(() => {
    if (product?.alert?.priceStep) {
      return String(product.alert.priceStep);
    }

    return String(defaultPriceStep);
  });

  useEffect(() => {
    if (emailInput.current && shouldAutoFocus) {
      emailInput.current.focus();
    }
  }, [shouldAutoFocus]);

  const clearErrors = (): void => setErrors(new Set());

  const addError = (newError: Errors) => {
    setErrors(prevErrors => {
      const draftErrors = new Set<Errors>(prevErrors);
      return draftErrors.add(newError);
    });
  };

  useEffect(() => {
    if (!error) {
      return;
    }

    switch (error) {
      case Errors.ERROR_THRESHOLD_TOO_HIGH:
      case Errors.ERROR_EMAIL_INVALID:
      case Errors.ERROR_EMAIL_IN_USE: {
        addError(error);
        break;
      }
      default: {
        addError(Errors.ERROR_UNKNOWN);
      }
    }
  }, [error]);

  const handlePriceThresholdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceThreshold(event.target.value);
  };

  const handlePriceStepChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceStep(event.target.value);
  };

  const handleOnlyAlertIfInStockChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setOnlyAlertIfInStock(event.target.checked);

  const handlePriceStepCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => setPriceStepCheckbox(event.target.checked);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setEmail(event.target.value),
    []
  );

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== RadioSelections.PRICE_BELOW) {
      setPriceStepCheckbox(false);
      setPriceStep(String(defaultPriceStep));
    }
    setRadioSelection(event.target.value as RadioSelections);
  };

  const saveAlert = useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();

      const { rating = 0 } = serialize(event.target, {
        hash: true,
      });

      const userAlert = userProductAlerts.find(
        alert => alert.productId === product?.id
      );

      if (!product) {
        return;
      }

      clearErrors();

      if (
        product.priceSummary.regular &&
        priceThreshold &&
        Number(priceThreshold) >= product.priceSummary.regular
      ) {
        addError(Errors.ERROR_THRESHOLD_TOO_HIGH);
        return;
      }

      if (!isAuthenticated && !isValidEmail(email)) {
        addError(Errors.ERROR_EMAIL_INVALID);
        if (shouldAutoFocus && emailInput.current) {
          emailInput.current.focus();
          emailInput.current.select();
        }
        return;
      }

      if (Number(priceThreshold) < 0 || Number(priceStep) < 0) {
        return;
      }

      const calculateGraphQLAlertType = (): GraphQLAlertType | null => {
        // Input
        // One of : IN_STOCK, PRICE_DROP, PRICE_BELOW
        // Output
        // { type: One of 'in_stock', 'price_drops', 'in_stock_price_drops', priceThreshold?: number  }
        switch (radioSelection) {
          case RadioSelections.IN_STOCK:
            return { type: AlertTypes.IN_STOCK };
          case RadioSelections.PRICE_DROPS:
            return {
              type: onlyAlertIfInStock
                ? AlertTypes.IN_STOCK_PRICE_DROPS
                : AlertTypes.PRICE_DROPS,
            };
          case RadioSelections.PRICE_BELOW:
            return {
              type: onlyAlertIfInStock
                ? AlertTypes.IN_STOCK_PRICE_DROPS
                : AlertTypes.PRICE_DROPS,
              priceThreshold: Number(priceThreshold),
            };
          default:
            return null;
        }
      };

      const graphQLAlert = calculateGraphQLAlertType();
      const priceStepValue =
        radioSelection === RadioSelections.PRICE_BELOW && priceStepCheckbox
          ? Number(priceStep)
          : undefined;
      if (graphQLAlert) {
        onCreate({
          alertId: userAlert && userAlert.alertId,
          productId: product.id,
          email: !isAuthenticated ? email : undefined,
          priceStep: priceStepValue,
          storeGradeThreshold: rating ? Number(rating) : undefined,
          ...graphQLAlert,
        });
      }
    },
    [
      email,
      onCreate,
      onlyAlertIfInStock,
      priceStep,
      priceStepCheckbox,
      priceThreshold,
      product,
      radioSelection,
      shouldAutoFocus,
      userProductAlerts,
      isAuthenticated,
    ]
  );

  const renderTypeOptions = () => {
    return (
      <>
        <Section>
          <S.Header>
            <T domain="alerts">alert-form.alert-me-when</T>
          </S.Header>
          {!productInStock && (
            <S.Radio
              name="radioSelection"
              id="alert-type-back-in-stock"
              label={t('alerts', 'type.back-in-stock')}
              value={RadioSelections.IN_STOCK}
              checked={radioSelection === RadioSelections.IN_STOCK}
              onChange={handleRadioChange}
            />
          )}

          <S.Radio
            name="radioSelection"
            id="alert-type-price-drop"
            label={t('alerts', 'type.price-drop')}
            value={RadioSelections.PRICE_DROPS}
            checked={radioSelection === RadioSelections.PRICE_DROPS}
            onChange={handleRadioChange}
          />

          <LoginPromoButton onClick={!isAuthenticated ? openLoginPromo : noop}>
            <S.PriceBelowWrapper>
              <S.Radio
                name="radioSelection"
                id="alert-type-price-below"
                label={t('alerts', 'type.price-below')}
                disabled={!isAuthenticated}
                value={RadioSelections.PRICE_BELOW}
                checked={radioSelection === RadioSelections.PRICE_BELOW}
                onChange={handleRadioChange}
              />
              <S.PriceInputWrapper>
                <S.NumberInput
                  name="priceThreshold"
                  ariaLabel={t('alerts', 'alert-form.price-limit')}
                  disabled={
                    radioSelection !== RadioSelections.PRICE_BELOW ||
                    !isAuthenticated
                  }
                  warning={Number(priceThreshold) < 0}
                  value={priceThreshold}
                  onChange={handlePriceThresholdChange}
                  step={minimumStepValue}
                  max={
                    product?.priceSummary.regular
                      ? parseFloat(product.priceSummary.regular.toString())
                      : undefined
                  }
                  min={0}
                  data-testid="price-threshold-number-input"
                />
                <S.MarketCurrency>{context.market.currency}</S.MarketCurrency>
              </S.PriceInputWrapper>
            </S.PriceBelowWrapper>
            <S.PriceStepWrapper showAlertPriceStep={showAlertPriceStep}>
              <S.PriceStepCheckbox
                name="priceStepCheckbox"
                checked={priceStepCheckbox}
                onChange={handlePriceStepCheckboxChange}
                disabled={
                  radioSelection !== RadioSelections.PRICE_BELOW ||
                  !isAuthenticated
                }
              >
                <T domain="alerts">alert-form.price-step-checkbox</T>
              </S.PriceStepCheckbox>
              <S.PriceInputWrapper>
                <S.NumberInput
                  name="priceStep"
                  ariaLabel={t('alerts', 'alert-form.price-interval')}
                  data-testid="price-step-number-input"
                  disabled={
                    radioSelection !== RadioSelections.PRICE_BELOW ||
                    !priceStepCheckbox ||
                    !isAuthenticated
                  }
                  warning={Number(priceStep) < 0}
                  value={priceStep}
                  onChange={handlePriceStepChange}
                  step={minimumStepValue}
                  max={
                    product?.priceSummary.regular
                      ? parseFloat(product.priceSummary.regular.toString())
                      : undefined
                  }
                  min={0}
                />
                <S.MarketCurrency>{context.market.currency}</S.MarketCurrency>
              </S.PriceInputWrapper>
            </S.PriceStepWrapper>
          </LoginPromoButton>
        </Section>

        {radioSelection !== RadioSelections.IN_STOCK && (
          <S.Checkbox
            name="onlyAlertIfInStock"
            checked={onlyAlertIfInStock}
            onChange={handleOnlyAlertIfInStockChange}
          >
            <T domain="alerts">alert-form.only-notify-in-stock</T>
          </S.Checkbox>
        )}

        <S.ShopsFilterWrapper
          showAlertStoreRating={showAlertStoreRating}
          isAuthenticated={isAuthenticated}
        >
          <S.Header>
            <T domain="alerts">alert-form.shops-with-higher-rating</T>
          </S.Header>
          <S.RatingWrapper>
            <LoginPromoButton
              onClick={!isAuthenticated ? openLoginPromo : noop}
            >
              <Rating
                rate={product?.alert?.storeGradeThreshold}
                interactive={isAuthenticated}
                onChange={rating => setChosenStoreGradeThreshold(rating)}
              />
            </LoginPromoButton>
            <p>
              {t('alerts', 'alert-form.n-stores', {
                n: numberOfStoresAboveRating(
                  product?.prices?.nodes,
                  chosenStoreGradeThreshold
                ),
              })}
            </p>
          </S.RatingWrapper>
        </S.ShopsFilterWrapper>
      </>
    );
  };

  return (
    <S.AlertForm method="post" onSubmit={saveAlert} noValidate>
      {renderTypeOptions()}
      <S.EmailInput error={errors.has(Errors.ERROR_EMAIL_INVALID)}>
        {!isAuthenticated && (
          <>
            <TextInput
              type="email"
              name="email"
              inputRef={emailInput}
              defaultValue={email}
              onChange={handleEmailChange}
              placeholder={t('alerts', 'alert-form.email-placeholder')}
              tabIndex={0}
              autoFocus={shouldAutoFocus}
            />
            <S.EmailHint visible={errors.has(Errors.ERROR_EMAIL_IN_USE)}>
              <T domain="alerts">alert-form.email-in-use-error</T>
              &nbsp;
              <CtxLink to="/login">
                <T domain="alerts">alert-form.login-error</T>
              </CtxLink>
            </S.EmailHint>
            <S.EmailHint error visible={errors.has(Errors.ERROR_EMAIL_INVALID)}>
              <T domain="alerts">alert-form.invalid-email-error</T>
            </S.EmailHint>
            <S.EmailHint error visible={errors.has(Errors.ERROR_UNKNOWN)}>
              <T domain="alerts">alert-form.unknown-error</T>
            </S.EmailHint>
            {privacyPolicyUri && (
              <S.EmailHint visible discreetLink>
                <CtxLink to={privacyPolicyUri}>
                  <T domain="product">alert.how-your-email-is-used</T>
                </CtxLink>
              </S.EmailHint>
            )}
          </>
        )}
      </S.EmailInput>
      <div>
        {product?.statistics && (
          <PriceTrend
            withoutGraph={withoutGraph}
            productAlertFormData={productAlertFormData}
          />
        )}
        <S.SaveAlertButton
          color="primary"
          disabled={disabled}
          iconPosition="left"
          data-testid="CreateAlertButton"
          data-test="CreateAlertButton"
          type="submit"
        >
          <T domain="alerts">alert-form.create-button</T>
        </S.SaveAlertButton>
      </div>
      {showCancel && product && (
        <S.CancelButton>
          <Link to={product && product.pathName}>
            <T domain="alerts">alert-form.cancel-button</T>
          </Link>
        </S.CancelButton>
      )}
    </S.AlertForm>
  );
};

const mapStateToProps = (state: AppState) => ({
  account: state.areas.account,
  userProductAlerts: state.areas.sidebar.alerts.products,
});

export default connect(mapStateToProps)(memo(ProductAlertForm));
